<template>
	<div class="g-block-pay-image-gallery" :class="{
		'g-block-pay-image-gallery--edit': editable,
		'is-active': editable && active,
		'g-block-pay-image-gallery--h5': h5,
		'g-block-pay-image-gallery--unlocked': isUnlocked,
	}" :style="rootStyle" @click="onWrapClick">
		<div class="g-block-pay-image-gallery__card">
			<div class="g-block-pay-image-gallery__header">
				<div class="g-block-pay-image-gallery__header-main">
					<div class="g-block-pay-image-gallery__title">{{ galleryData.title }}</div>
					<div class="g-block-pay-image-gallery__subtitle">{{ subtitleText }}</div>
					<div class="g-block-pay-image-gallery__features">
						<span v-for="feature in galleryData.features" :key="feature.id"
							class="g-block-pay-image-gallery__feature">
							<i class="g-block-pay-image-gallery__feature-icon"
								:class="featureIconClass(feature.icon)"></i>
							<span>{{ feature.text }}</span>
						</span>
					</div>
				</div>
				<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-image-gallery__header-action">
					<div class="g-block-pay-image-gallery__price">
						<span class="g-block-pay-image-gallery__currency">{{ galleryData.currency }}</span>
						<span class="g-block-pay-image-gallery__price-value">{{ galleryData.price }}</span>
					</div>
					<button type="button" class="g-block-pay-image-gallery__unlock-btn" :style="buttonStyle"
						@click.stop="onUnlockClick">{{ galleryData.buttonText }}</button>
				</div>
			</div>

			<!-- <div v-if="!isUnlocked && showInfoBar" class="g-block-pay-image-gallery__info">
				<SoundOutlined class="g-block-pay-image-gallery__info-icon" />
				<span>
					解锁后可查看全部
					<strong>{{ totalCount }}</strong>
					张图片
				</span>
			</div> -->

			<div class="g-block-pay-image-gallery__grid">
				<div v-for="(item, index) in galleryData.items" :key="item.id" class="g-block-pay-image-gallery__cell">
					<div class="g-block-pay-image-gallery__thumb" :class="{
						'is-locked': isItemLocked(index),
						'is-previewable': isItemPreviewable(index),
					}" :style="thumbStyle" @click="onThumbClick(index, $event)">
						<img v-if="getItemImageUrl(item)" :src="getItemImageUrl(item)" alt="" />
						<i v-else class="mbm-iconfont mbm-tupian"></i>
						<span class="g-block-pay-image-gallery__index">{{ formatIndex(index) }}</span>
						<div v-if="isItemLocked(index)" class="g-block-pay-image-gallery__lock-mask">
							<i class="mbm-iconfont mbm-suoding g-block-pay-image-gallery__lock-icon"></i>
							<!-- <span class="g-block-pay-image-gallery__lock-text">解锁后可查看</span> -->
						</div>
					</div>
				</div>
			</div>
		</div>

		<ImageGalleryLightbox v-model:visible="lightboxVisible" :urls="lightboxUrls" :initial-index="lightboxIndex" />
	</div>
</template>

<script>
import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
import { SoundOutlined } from '@ant-design/icons-vue';
import {
	getPayImageGalleryBlockStyle,
	getPayImageGalleryGalleryUrls,
	getPayImageGalleryImageUrl,
	isPayImageGalleryUnlocked,
	normalizePayImageGalleryData,
	setPayImageGalleryUnlocked,
} from '../../../utils/payImageGallery.js';

export default {
	name: 'GPayImageGalleryShell',
	mixins: [geditPayUnlockMixin],
	components: { ImageGalleryLightbox, SoundOutlined },
	props: {
		data: {
			type: Object,
			required: true,
		},
		blockId: {
			type: String,
			default: '',
		},
		editable: {
			type: Boolean,
			default: false,
		},
		active: {
			type: Boolean,
			default: false,
		},
		h5: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['select'],
	data() {
		return {
			unlockedLocal: false,
			lightboxVisible: false,
			lightboxIndex: 0,
		};
	},
	computed: {
		galleryData() {
			return normalizePayImageGalleryData(this.data);
		},
		blockStyle() {
			return getPayImageGalleryBlockStyle(this.galleryData, { h5: this.h5 });
		},
		rootStyle() {
			const style = this.blockStyle;
			return {
				'--pay-gallery-col-gap': style.colGap,
				'--pay-gallery-side-inset': '0',
				'--pay-gallery-radius': style.borderRadius,
			};
		},
		thumbStyle() {
			return {
				aspectRatio: this.blockStyle.aspectRatio,
				borderRadius: this.blockStyle.borderRadius,
			};
		},
		buttonStyle() {
			return {
				background: this.galleryData.buttonBg,
			};
		},
		totalCount() {
			return this.galleryData.items.length;
		},
		freePreviewCount() {
			return Math.min(this.galleryData.freePreviewCount, this.totalCount);
		},
		subtitleText() {
			const subtitle = this.galleryData.subtitle || '';
			if (/\d+/.test(subtitle)) {
				return subtitle.replace(/\d+/, String(this.totalCount));
			}
			return `共 ${this.totalCount} 张精选高清壁纸`;
		},
		isUnlocked() {
			if (this.editable) return false;
			return this.unlockedLocal || isPayImageGalleryUnlocked(this.blockId) || this.isGeditModulePaid;
		},
		showInfoBar() {
			return this.freePreviewCount > 0 && this.freePreviewCount < this.totalCount;
		},
		lightboxUrls() {
			if (this.isUnlocked) {
				return getPayImageGalleryGalleryUrls(this.galleryData);
			}
			return getPayImageGalleryGalleryUrls(this.galleryData).slice(0, this.freePreviewCount);
		},
	},
	watch: {
		blockId: {
			immediate: true,
			handler() {
				this.syncUnlockState();
			},
		},
	},
	methods: {
		syncUnlockState() {
			this.unlockedLocal = isPayImageGalleryUnlocked(this.blockId);
		},
		getItemImageUrl(item) {
			return getPayImageGalleryImageUrl(item?.image);
		},
		formatIndex(index) {
			return String(index + 1).padStart(2, '0');
		},
		featureIconClass(icon) {
			const map = {
				check: 'mbm-iconfont mbm-check is-check',
				download: 'mbm-iconfont mbm-xiazai1 is-download',
				shield: 'mbm-iconfont mbm-anquan is-shield',
			};
			return map[icon] || map.check;
		},
		isItemLocked(index) {
			if (this.isUnlocked) return false;
			return index >= this.freePreviewCount;
		},
		isItemPreviewable(index) {
			if (this.editable) return false;
			if (this.isUnlocked) return Boolean(this.getItemImageUrl(this.galleryData.items[index]));
			return index < this.freePreviewCount && Boolean(this.getItemImageUrl(this.galleryData.items[index]));
		},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
		onUnlockClick() {
			this.runGeditUnlockFlow({
				applyUnlock: () => {
					setPayImageGalleryUnlocked(this.blockId, true);
					this.unlockedLocal = true;
				},
			});
		},
		onThumbClick(index, event) {
			if (this.isItemLocked(index)) {
				this.onUnlockClick();
				return
			}
			if (!this.isItemPreviewable(index)) return;
			event.stopPropagation();
			this.lightboxIndex = index;
			this.lightboxVisible = true;
		},
	},
};
</script>

<style scoped lang="less">
.g-block-pay-image-gallery {
	box-sizing: border-box;
	padding-left: var(--pay-gallery-side-inset, 0);
	padding-right: var(--pay-gallery-side-inset, 0);

	&--edit {
		border: 1px solid transparent;
		border-radius: 4px;
		transition: border-color 0.2s, box-shadow 0.2s;
		cursor: pointer;

		&.is-active {
			border-color: #285af9;
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
		}
	}
}

.g-block-pay-image-gallery__card {
	background: #fff;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
}

.g-block-pay-image-gallery__header {
	display: flex;
	align-items: end;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 10px 10px;
}

.g-block-pay-image-gallery__header-main {
	flex: 1;
	min-width: 0;
}

.g-block-pay-image-gallery__title {
	font-size: 18px;
	font-weight: 700;
	color: #1a1a1a;
	line-height: 1.35;
}

.g-block-pay-image-gallery__subtitle {
	margin-top: 4px;
	font-size: 13px;
	color: #999;
	line-height: 1.4;
}

.g-block-pay-image-gallery__features {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 8px;
	margin-bottom: 4px;
}

.g-block-pay-image-gallery__feature {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: #666;
	line-height: 1;
}

.g-block-pay-image-gallery__feature-icon {
	font-size: 12px;

	&.is-check {
		color: #52c41a;
	}

	&.is-download {
		color: #285af9;
	}

	&.is-shield {
		color: #faad14;
	}
}

.g-block-pay-image-gallery__header-action {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
}

.g-block-pay-image-gallery__price {
	display: flex;
	align-items: baseline;
	color: #ff4d4f;
	line-height: 1;
}

.g-block-pay-image-gallery__currency {
	font-size: 14px;
	font-weight: 600;
	margin-right: 2px;
}

.g-block-pay-image-gallery__price-value {
	font-size: 22px;
	font-weight: 700;
}

.g-block-pay-image-gallery__unlock-btn {
	margin-top: 10px;
	margin-bottom: 4px;
	border: none;
	border-radius: 999px;
	padding: 5px 14px;
	font-size: 11px;
	font-weight: normal;
	color: #fff;
	cursor: pointer;
	white-space: nowrap;
	line-height: 1.3;

	&:active {
		opacity: 0.9;
	}
}

.g-block-pay-image-gallery__info {
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 0 10px 10px;
	padding: 8px 10px;
	background: #eef4ff;
	border-radius: 8px;
	font-size: 12px;
	color: #666;
	line-height: 1.5;

	strong {
		color: #285af9;
		font-weight: 600;
	}
}

.g-block-pay-image-gallery__info-icon {
	flex-shrink: 0;
	font-size: 14px;
	color: #285af9;
}

.g-block-pay-image-gallery__grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: var(--pay-gallery-col-gap, 5px);
	padding: 0 10px 14px;
}

.g-block-pay-image-gallery__thumb {
	position: relative;
	overflow: hidden;
	background: #f5f5f5;

	img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.mbm-tupian {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		color: #ccc;
	}

	&.is-previewable {
		cursor: pointer;
	}
}

.g-block-pay-image-gallery__index {
	position: absolute;
	top: 6px;
	left: 6px;
	z-index: 2;
	padding: 2px 6px;
	border-radius: 4px;
	background: rgba(0, 0, 0, 0.35);
	color: #fff;
	font-size: 11px;
	line-height: 1.2;
}

.g-block-pay-image-gallery__lock-mask {
	position: absolute;
	inset: 0;
	z-index: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	background: rgba(0, 0, 0, 0.15);
}

.g-block-pay-image-gallery__lock-icon {
	font-size: 20px;
	color: #fff;
}

.g-block-pay-image-gallery__lock-text {
	font-size: 9px;
	color: #fff;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.g-block-pay-image-gallery__thumb.is-locked img {
	filter: blur(6px);
	transform: scale(1.08);
}

.g-block-pay-image-gallery--h5 {
	.g-block-pay-image-gallery__card {
		border-radius: 0.24rem;
		box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-image-gallery__header {
		gap: 0.24rem;
		padding: 0.28rem 0.2rem 0.2rem;
	}

	.g-block-pay-image-gallery__title {
		font-size: 0.36rem;
	}

	.g-block-pay-image-gallery__subtitle {
		margin-top: 0.08rem;
		font-size: 0.26rem;
	}

	.g-block-pay-image-gallery__features {
		gap: 0.2rem;
		margin-top: 0.16rem;
		margin-bottom: 0.08rem;
	}

	.g-block-pay-image-gallery__feature {
		gap: 0.08rem;
		font-size: 0.24rem;
	}

	.g-block-pay-image-gallery__feature-icon {
		font-size: 0.24rem;
	}

	.g-block-pay-image-gallery__currency {
		font-size: 0.28rem;
	}

	.g-block-pay-image-gallery__price-value {
		font-size: 0.44rem;
	}

	.g-block-pay-image-gallery__unlock-btn {
		padding: 0.1rem 0.28rem;
		font-size: 0.22rem;
		margin-top: 0.2rem;
		margin-bottom: 0.08rem;
	}

	.g-block-pay-image-gallery__info {
		gap: 0.12rem;
		margin: 0 0.2rem 0.2rem;
		padding: 0.16rem 0.2rem;
		border-radius: 0.16rem;
		font-size: 0.24rem;
	}

	.g-block-pay-image-gallery__info-icon {
		font-size: 0.28rem;
	}

	.g-block-pay-image-gallery__grid {
		padding: 0 0.20rem 0.28rem;
	}

	.g-block-pay-image-gallery__index {
		top: 0.12rem;
		left: 0.12rem;
		padding: 0.04rem 0.12rem;
		border-radius: 0.08rem;
		font-size: 0.22rem;
	}

	.g-block-pay-image-gallery__lock-icon {
		font-size: 0.4rem;
	}

	.g-block-pay-image-gallery__lock-text {
		font-size: 0.18rem;
	}

	.g-block-pay-image-gallery__lock-mask {
		gap: 0.08rem;
	}
}
</style>
